import { KeyboardCode, KeyboardCoordinateGetter } from '@dnd-kit/core';
import { KeyboardDropTarget, ResolveNextKeyboardTargetParams } from './catalogDragAndDrop.types';

const VERTICAL_KEYS: string[] = [KeyboardCode.Up, KeyboardCode.Down];

export const resolveNextKeyboardTarget = ({
  activeId,
  currentTarget,
  itemIds,
  isMovingDown,
}: ResolveNextKeyboardTargetParams): KeyboardDropTarget | null => {
  const anchorIndex = itemIds.indexOf(currentTarget?.overId ?? activeId);

  if (anchorIndex === -1) {
    return null;
  }

  const step = isMovingDown ? 1 : -1;
  let nextIndex = anchorIndex + step;

  if (itemIds[nextIndex] === activeId) {
    nextIndex += step;
  }

  const overId = itemIds[nextIndex];

  if (!overId) {
    return null;
  }

  return { overId, side: isMovingDown ? 'after' : 'before' };
};

interface CreateCatalogKeyboardCoordinateGetterParams {
  getItemIds: () => string[];
  getTarget: () => KeyboardDropTarget | null;
  onTargetChange: (target: KeyboardDropTarget) => void;
}

export const createCatalogKeyboardCoordinateGetter =
  ({
    getItemIds,
    getTarget,
    onTargetChange,
  }: CreateCatalogKeyboardCoordinateGetterParams): KeyboardCoordinateGetter =>
  (event, { context }) => {
    const { active, collisionRect, droppableRects } = context;

    if (!VERTICAL_KEYS.includes(event.code) || !active || !collisionRect) {
      return undefined;
    }

    event.preventDefault();

    const nextTarget = resolveNextKeyboardTarget({
      activeId: String(active.id),
      currentTarget: getTarget(),
      itemIds: getItemIds(),
      isMovingDown: event.code === KeyboardCode.Down,
    });

    const targetRect = nextTarget ? droppableRects.get(nextTarget.overId) : undefined;

    if (!nextTarget || !targetRect) {
      return undefined;
    }

    onTargetChange(nextTarget);

    return {
      x: targetRect.left,
      y: targetRect.top + targetRect.height / 2 - collisionRect.height / 2,
    };
  };
